import {
  ABILITY_DEFS,
  ARCHETYPE_ABILITY,
  ARCHETYPE_SECONDARY_ABILITY,
} from '../../data/abilities.js';
import type { AbilityDef, AbilityId } from '../../data/abilities.js';
import {
  CUT_LINK_DURATION_SEC,
  CUT_LINK_TARGET_RANGE_SQ,
} from '../../data/cutLink.js';
import {
  DISRUPT_UPLINK_DURATION_SEC,
  DISRUPT_UPLINK_TARGET_RANGE_SQ,
} from '../../data/disruptUplink.js';
import { SABOTEUR_MARK_DAMAGE_FRAC } from '../../data/doctrines.js';
import {
  FORTIFY_RELAY_DURATION_SEC,
  FORTIFY_RELAY_TARGET_RANGE_SQ,
} from '../../data/fortifyRelay.js';
import {
  JAM_RELAY_DURATION_SEC,
  JAM_RELAY_TARGET_RANGE_SQ,
} from '../../data/jamRelay.js';
import type { MatchState } from '../State.js';
import { SECONDS_PER_TICK } from '../State.js';
import type { AbilityCommand, AbilityTargetRef } from '../commands/AbilityCommand.js';
import type { MiniTurret } from '../entities/Building.js';
import type { ControlNode } from '../entities/ControlNode.js';
import type { Squad } from '../entities/Squad.js';
import type { EntityId, PlayerId } from '../ids.js';
import { entityId, tickOf } from '../ids.js';

import { routeDamage } from './damageRouting.js';
export { absorbFromBubble } from './damageRouting.js';

// AbilitySystem responsibilities each tick:
//   1. Decrement Squad.abilityCooldown AND Squad.secondaryAbilityCooldown
//      (V1.5+ Slice 2: Recon's E-slot Cut Link is on its own cooldown).
//   2. Reap expired ShieldBubbles (remaining ≤ 0 OR expired tick).
//   3. Reap expired mini-turrets (state.tick ≥ expiresAtTick); hp ≤ 0 is handled
//      by BuildingSystem alongside the other building reaps.
//   4. Clear stale 'marked' / 'suppressed' tags so the next tick's combat /
//      movement systems see a clean view.
//   5. V1.5+ Slice 2: complete any squad whose `channelingAbility` window
//      expired this tick — applies the channeled effect (currently only
//      Cut Link sets `node.linkCutUntilTick`) and clears the channel state.
//
// Ability *application* (mark, spawn turret, spawn bubble, suppress enemies) lives
// in applyAbilityCommand below, called by Simulation.applyCommand. This mirrors
// produce_squad / build: applyCommand does the atomic state mutation; the matching
// system does per-tick advancement and cleanup.
export function abilitySystem(state: MatchState): void {
  for (const sq of Object.values(state.squads)) {
    if (sq.abilityCooldown > 0) {
      sq.abilityCooldown = Math.max(0, sq.abilityCooldown - SECONDS_PER_TICK);
    }
    if (sq.secondaryAbilityCooldown > 0) {
      sq.secondaryAbilityCooldown = Math.max(0, sq.secondaryAbilityCooldown - SECONDS_PER_TICK);
    }
    if (sq.markedUntilTick > 0 && state.tick >= sq.markedUntilTick) {
      sq.markedUntilTick = tickOf(0);
      sq.markedByPlayerId = null;
    }
    if (sq.suppressedUntilTick > 0 && state.tick >= sq.suppressedUntilTick) {
      sq.suppressedUntilTick = tickOf(0);
    }
    completeChannelIfExpired(state, sq);
  }
  reapExpiredBubbles(state);
  reapExpiredMiniTurrets(state);
}

// V1.5+ Slice 2 — public so damageRouting / Simulation.applyCommand can cancel
// a squad's channel mid-flight without importing the system internals. Clears
// the channel state without applying any effect (the cut never lands). Idempotent
// when there's no active channel.
export function cancelChanneling(squad: Squad): void {
  if (squad.channelingAbility === null) return;
  squad.channelingAbility = null;
}

function completeChannelIfExpired(state: MatchState, squad: Squad): void {
  const ch = squad.channelingAbility;
  if (ch === null) return;
  if (state.tick < ch.expiresAtTick) return;
  // Channel reached its expiry tick — apply the effect and clear the channel.
  // Channeled abilities: Cut Link (Slice 2), Jam Relay (Slice 5),
  // Disrupt Uplink (Slice 6).
  if (ch.abilityId === 'cut_link') {
    applyCutLink(state, squad, ch.targetEntityId);
  } else if (ch.abilityId === 'jam_relay') {
    applyJamRelay(state, squad, ch.targetEntityId);
  } else if (ch.abilityId === 'disrupt_uplink') {
    applyDisruptUplink(state, squad, ch.targetEntityId);
  } else if (ch.abilityId === 'fortify_relay') {
    applyFortifyRelay(state, squad, ch.targetEntityId);
  }
  squad.channelingAbility = null;
}

// V1.5+ Slice 2 — read by MovementSystem / CombatSystem to short-circuit a
// channeling squad's actions. Centralised so the rule lives next to the
// channel state owner.
export function isChanneling(squad: Squad): boolean {
  return squad.channelingAbility !== null;
}

function applyCutLink(state: MatchState, caster: Squad, nodeId: EntityId): void {
  const node: ControlNode | undefined = state.controlNodes[nodeId];
  if (node === undefined) return;
  // V1.5+ Slice 7 — Fortify Relay immunity. A fortified relay rejects
  // incoming cut/jam/disrupt APPLY (the channel still completed, the
  // attacker still pays the cooldown — fortify is a counter-window, not
  // a deflection of the channel itself). Self-cuts on a self-fortified
  // relay are also blocked: the immunity is per-relay, not per-attribution.
  if (isFortified(state, node)) return;
  // The cut window starts NOW and lasts CUT_LINK_DURATION_SEC. Subsequent
  // channels by either player extend (overwrite) the window — the latest
  // attribution wins, which is the right behaviour for telemetry: the player
  // currently denying the network is the one who paid the channel cost.
  const durationTicks = secondsToTicks(CUT_LINK_DURATION_SEC);
  node.linkCutUntilTick = tickOf(state.tick + durationTicks);
  node.linkCutByPlayerId = caster.ownerId;
  state.stats.cutLinksAppliedByPlayer[caster.ownerId] += 1;
}

// V1.5+ Slice 5 — Jam Relay application. Mirrors `applyCutLink`'s shape, but
// writes to a different field on the node so SignalLinkSystem's BFS still
// traverses through the jammed node. The resulting differential behaviour
// (vision drops, chain stays intact) is the slice's mechanical distinctness
// from Cut Link.
function applyJamRelay(state: MatchState, caster: Squad, nodeId: EntityId): void {
  const node: ControlNode | undefined = state.controlNodes[nodeId];
  if (node === undefined) return;
  // V1.5+ Slice 7 — Fortify Relay immunity (see applyCutLink for the rule).
  if (isFortified(state, node)) return;
  // Latest-attribution-wins, same convention as cut. A jam landed by player A
  // followed by a re-jam from player B before A's window expires gives B the
  // attribution — matches "the player currently denying the network".
  const durationTicks = secondsToTicks(JAM_RELAY_DURATION_SEC);
  node.jammedUntilTick = tickOf(state.tick + durationTicks);
  node.jammedByPlayerId = caster.ownerId;
  state.stats.jamRelaysAppliedByPlayer[caster.ownerId] += 1;
}

// V1.5+ Slice 6 — Disrupt Uplink application. Mirrors `applyCutLink` /
// `applyJamRelay`'s shape; writes to the disrupted-* fields on the node.
// CombatSystem reads `disruptedUntilTick` + `disruptedByPlayerId` per tick
// and degrades the fire rate / range of any anchored mini_turret whose
// owner is opposite the disruptor.
function applyDisruptUplink(state: MatchState, caster: Squad, nodeId: EntityId): void {
  const node: ControlNode | undefined = state.controlNodes[nodeId];
  if (node === undefined) return;
  // V1.5+ Slice 7 — Fortify Relay immunity (see applyCutLink for the rule).
  if (isFortified(state, node)) return;
  // Latest-attribution-wins, same convention as cut / jam.
  const durationTicks = secondsToTicks(DISRUPT_UPLINK_DURATION_SEC);
  node.disruptedUntilTick = tickOf(state.tick + durationTicks);
  node.disruptedByPlayerId = caster.ownerId;
  state.stats.disruptUplinksAppliedByPlayer[caster.ownerId] += 1;
}

// V1.5+ Slice 7 — Fortify Relay application. Mirrors `applyCutLink` /
// `applyJamRelay` / `applyDisruptUplink`'s shape; writes to the fortified-*
// fields on the node. CombatSystem reads `fortifiedUntilTick` +
// `fortifiedByPlayerId` per tick and buffs the fire rate of any anchored
// mini_turret whose owner matches the fortifier (the inverse condition of
// disrupt — a fortify by your OWN side buffs your turrets; the §15 dynamic
// is "this is MY relay, my turrets fire harder while the window holds").
function applyFortifyRelay(state: MatchState, caster: Squad, nodeId: EntityId): void {
  const node: ControlNode | undefined = state.controlNodes[nodeId];
  if (node === undefined) return;
  // Self-fortifying a relay you don't own is allowed at the channel-start
  // level (no validation in startFortifyRelayChannel), but the fire-rate
  // buff in CombatSystem only fires when fortifiedByPlayerId === turret
  // owner — so a fortify on a neutral or enemy relay simply has no
  // observable effect. The immunity property still works (cut/jam/disrupt
  // can't land for the window), which is a reasonable strategic option:
  // a Shield can fortify a contested relay it's about to capture to deny
  // the enemy a last-second cut.
  const durationTicks = secondsToTicks(FORTIFY_RELAY_DURATION_SEC);
  node.fortifiedUntilTick = tickOf(state.tick + durationTicks);
  node.fortifiedByPlayerId = caster.ownerId;
  state.stats.fortifyRelaysAppliedByPlayer[caster.ownerId] += 1;
}

// V1.5+ Slice 7 — true if the relay is currently inside a fortify window
// (active ByPlayer + state.tick before expiry). Used by applyCutLink /
// applyJamRelay / applyDisruptUplink to no-op on a fortified relay.
function isFortified(state: MatchState, node: ControlNode): boolean {
  if (node.fortifiedByPlayerId === null) return false;
  return state.tick < node.fortifiedUntilTick;
}

// Returns true if applied successfully (state was mutated). Returns false if the
// command failed validation (squad missing, cooldown active, owner mismatch,
// target out of range, etc.). The host drops failed commands silently.
export function applyAbilityCommand(state: MatchState, command: AbilityCommand): boolean {
  const squad = state.squads[command.squadId];
  if (squad === undefined) return false;
  if (squad.ownerId !== command.issuedBy) return false;
  // Channel-block: a squad mid-channel can't fire another ability (Q or E).
  // The interrupt path is damageRouting / movement command — not "fire a
  // second ability and expect the first to cancel".
  if (squad.channelingAbility !== null) return false;
  // Resolve the ability bound to the requested hotkey. Unknown hotkey
  // (defensive — wire format constrains it) or unbound secondary slot drop.
  const def = resolveAbilityFor(squad.archetype, command.hotkey);
  if (def === null) return false;
  // Cooldown gate per-hotkey: Q reads `abilityCooldown`, E reads
  // `secondaryAbilityCooldown`. This is the single place the two cooldown
  // fields are kept distinct so a Cut Link cast doesn't lock out Mark Target.
  const cooldown = command.hotkey === 'Q' ? squad.abilityCooldown : squad.secondaryAbilityCooldown;
  if (cooldown > 0) return false;
  if (def.requiresTarget && command.target === null) return false;
  let applied = false;
  switch (def.id) {
    case 'mark_target':
      applied = runMarkTarget(state, squad, def, command.target);
      break;
    case 'suppressing_fire':
      runSuppressingFire(state, squad, def);
      applied = true;
      break;
    case 'deploy_mini_turret':
      runDeployMiniTurret(state, squad, def);
      applied = true;
      break;
    case 'phase_shield':
      runPhaseShield(state, squad, def);
      applied = true;
      break;
    case 'cut_link':
      applied = startCutLinkChannel(state, squad, def, command.target);
      break;
    case 'jam_relay':
      applied = startJamRelayChannel(state, squad, def, command.target);
      break;
    case 'disrupt_uplink':
      applied = startDisruptUplinkChannel(state, squad, def, command.target);
      break;
    case 'fortify_relay':
      applied = startFortifyRelayChannel(state, squad, def, command.target);
      break;
  }
  if (!applied) return false;
  if (command.hotkey === 'Q') squad.abilityCooldown = def.cooldownSec;
  else squad.secondaryAbilityCooldown = def.cooldownSec;
  return true;
}

function resolveAbilityFor(
  archetype: Squad['archetype'],
  hotkey: 'Q' | 'E',
): AbilityDef | null {
  if (hotkey === 'Q') return ABILITY_DEFS[ARCHETYPE_ABILITY[archetype]];
  const id = ARCHETYPE_SECONDARY_ABILITY[archetype];
  if (id === undefined) return null;
  return ABILITY_DEFS[id];
}

// V1.5+ Slice 2 — Cut Link initiation. Validates the target is a relay within
// `cutLinkTargetRangePx` and the caster isn't already mid-channel; on success,
// arms `squad.channelingAbility` so MovementSystem freezes the squad and
// CombatSystem suppresses its attacks. The cut effect itself lands when
// AbilitySystem's `completeChannelIfExpired` reaches the expiry tick.
//
// Self-cuts (your own relay) are allowed by design — strategic choice, and
// gating them off here would invite "I forgot I owned that node" frustration.
// Cutting a contested or neutral relay is allowed; the cut applies to the
// node's conductivity regardless of ownership at completion time.
function startCutLinkChannel(
  state: MatchState,
  caster: Squad,
  def: AbilityDef,
  target: AbilityTargetRef | null,
): boolean {
  if (target === null || target.kind !== 'control_node') return false;
  const node = state.controlNodes[target.id];
  if (node === undefined) return false;
  const dSq = sqDist(caster.pos, node.pos);
  if (dSq > CUT_LINK_TARGET_RANGE_SQ) return false;
  const channelTicks = secondsToTicks(def.cutLinkChannelSec ?? def.durationSec);
  caster.channelingAbility = {
    abilityId: 'cut_link',
    targetEntityId: node.id,
    startedAtTick: tickOf(state.tick),
    expiresAtTick: tickOf(state.tick + channelTicks),
  };
  return true;
}

// V1.5+ Slice 5 — Jam Relay channel initiation. Mirrors `startCutLinkChannel`
// (Engineer must be within `jamRelayTargetRangePx` of a relay; channel arms
// `channelingAbility` so MovementSystem freezes the squad and CombatSystem
// suppresses its attacks). The MECHANICAL DIFFERENCE lands at completion via
// `applyJamRelay` writing to `node.jammedUntilTick` instead of
// `node.linkCutUntilTick`.
//
// Self-jams (your own relay) are allowed by design — strategic choice; gating
// here would invite "I forgot I owned that node" frustration. Cutting a
// neutral relay is allowed — the jam still lands; it just has no defender to
// alert (see CriticalFlipEmitter's `after.ownerId !== null` guard).
function startJamRelayChannel(
  state: MatchState,
  caster: Squad,
  def: AbilityDef,
  target: AbilityTargetRef | null,
): boolean {
  if (target === null || target.kind !== 'control_node') return false;
  const node = state.controlNodes[target.id];
  if (node === undefined) return false;
  const dSq = sqDist(caster.pos, node.pos);
  if (dSq > JAM_RELAY_TARGET_RANGE_SQ) return false;
  const channelTicks = secondsToTicks(def.jamRelayChannelSec ?? def.durationSec);
  caster.channelingAbility = {
    abilityId: 'jam_relay',
    targetEntityId: node.id,
    startedAtTick: tickOf(state.tick),
    expiresAtTick: tickOf(state.tick + channelTicks),
  };
  return true;
}

// V1.5+ Slice 6 — Disrupt Uplink channel initiation. Mirrors the shape of
// `startCutLinkChannel` / `startJamRelayChannel`. Self-disrupts (own relay)
// are allowed by design — strategic choice; gating here would invite "I
// forgot I owned that node" frustration. The disruption multiplier in
// CombatSystem only applies when `disruptedByPlayerId !== turret.ownerId`,
// so a self-disrupt is harmless effect-wise but still spends the cooldown.
function startDisruptUplinkChannel(
  state: MatchState,
  caster: Squad,
  def: AbilityDef,
  target: AbilityTargetRef | null,
): boolean {
  if (target === null || target.kind !== 'control_node') return false;
  const node = state.controlNodes[target.id];
  if (node === undefined) return false;
  const dSq = sqDist(caster.pos, node.pos);
  if (dSq > DISRUPT_UPLINK_TARGET_RANGE_SQ) return false;
  const channelTicks = secondsToTicks(def.disruptUplinkChannelSec ?? def.durationSec);
  caster.channelingAbility = {
    abilityId: 'disrupt_uplink',
    targetEntityId: node.id,
    startedAtTick: tickOf(state.tick),
    expiresAtTick: tickOf(state.tick + channelTicks),
  };
  return true;
}

// V1.5+ Slice 7 — Fortify Relay channel initiation. Mirrors the shape of
// startCutLinkChannel / startJamRelayChannel / startDisruptUplinkChannel.
// Foreign-relay fortifies (target owned by the enemy, or neutral) are
// allowed at this layer — applyFortifyRelay's effect is per-relay, and the
// fire-rate buff filters by `fortifiedByPlayerId === turret.ownerId`, so a
// fortify cast on someone else's relay simply has no buff effect (only the
// immunity property remains, which is a legitimate "deny incoming cut/jam/
// disrupt for 20 s" play). Gating here would invite "I forgot I owned that
// node" frustration and lose the strategic option.
function startFortifyRelayChannel(
  state: MatchState,
  caster: Squad,
  def: AbilityDef,
  target: AbilityTargetRef | null,
): boolean {
  if (target === null || target.kind !== 'control_node') return false;
  const node = state.controlNodes[target.id];
  if (node === undefined) return false;
  const dSq = sqDist(caster.pos, node.pos);
  if (dSq > FORTIFY_RELAY_TARGET_RANGE_SQ) return false;
  const channelTicks = secondsToTicks(def.fortifyRelayChannelSec ?? def.durationSec);
  caster.channelingAbility = {
    abilityId: 'fortify_relay',
    targetEntityId: node.id,
    startedAtTick: tickOf(state.tick),
    expiresAtTick: tickOf(state.tick + channelTicks),
  };
  return true;
}

function runMarkTarget(
  state: MatchState,
  caster: Squad,
  def: AbilityDef,
  target: AbilityTargetRef | null,
): boolean {
  if (target === null) return false;
  // V1 marks enemy squads only. Building/Core marks are reserved for future
  // §13 directions (Signal Tap, Jam Relay, etc.) which are V1.5+.
  if (target.kind !== 'squad') return false;
  const targetSquad = state.squads[target.id];
  if (targetSquad === undefined) return false;
  if (targetSquad.ownerId === caster.ownerId) return false;
  const range = def.markRange ?? 600;
  const dSq = sqDist(caster.pos, targetSquad.pos);
  if (dSq > range * range) return false;
  // V1.5 Saboteur: apply 5% maxHp damage on the cast tick BEFORE setting the
  // mark. Routing through routeDamage with applyMarkBonus=false means Phase
  // Shield absorbs and Core armor scaling apply (per docs/02_GAME_DESIGN.md §6
  // and the prompt's spec), but the +10% mark bonus does not self-apply.
  // (The mark hasn't been set yet anyway, but we pass the flag explicitly so
  // future maintainers don't confuse the order.)
  if (state.players[caster.ownerId].doctrineId === 'saboteur') {
    const sabDamage = SABOTEUR_MARK_DAMAGE_FRAC * targetSquad.maxHp;
    routeDamage(
      state,
      { kind: 'squad', id: targetSquad.id },
      sabDamage,
      caster.ownerId,
      { applyMarkBonus: false },
    );
  }
  const durationTicks = secondsToTicks(def.durationSec);
  targetSquad.markedUntilTick = tickOf(state.tick + durationTicks);
  targetSquad.markedByPlayerId = caster.ownerId;
  return true;
}

function runSuppressingFire(state: MatchState, caster: Squad, def: AbilityDef): void {
  const range = def.suppressionRange ?? 320;
  const halfAngle = def.suppressionHalfAngleRad ?? Math.PI / 4;
  const rangeSq = range * range;
  const durationTicks = secondsToTicks(def.durationSec);
  const expiry = tickOf(state.tick + durationTicks);
  for (const enemy of Object.values(state.squads)) {
    if (enemy.ownerId === caster.ownerId) continue;
    const dx = enemy.pos.x - caster.pos.x;
    const dy = enemy.pos.y - caster.pos.y;
    const dSq = dx * dx + dy * dy;
    if (dSq > rangeSq) continue;
    if (dSq > 0) {
      const targetAngle = Math.atan2(dy, dx);
      let diff = targetAngle - caster.facing;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      if (Math.abs(diff) > halfAngle) continue;
    }
    if (enemy.suppressedUntilTick < expiry) {
      enemy.suppressedUntilTick = expiry;
    }
  }
}

function runDeployMiniTurret(state: MatchState, caster: Squad, def: AbilityDef): void {
  const hp = def.miniTurretHp ?? 120;
  const durationSec = def.miniTurretDurationSec ?? 30;
  const id = mintEntityId(state);
  const turret: MiniTurret = {
    id,
    ownerId: caster.ownerId,
    kind: 'mini_turret',
    pos: { x: caster.pos.x, y: caster.pos.y },
    hp,
    maxHp: hp,
    buildProgress: 1,
    attackCooldown: 0,
    target: null,
    facing: caster.facing,
    expiresAtTick: tickOf(state.tick + secondsToTicks(durationSec)),
    // V1.5+ forward turret network dependency. Default true so the first
    // rendered frame after deploy doesn't flicker — ForwardTurretNetworkSystem
    // reconciles to false on the next tick if no owned, connected relay is
    // within FORWARD_TURRET_RANGE_PX. The single-tick "online" grace is
    // documented in the system's ordering comment.
    isOnline: true,
  };
  state.buildings[id] = turret;
}

function runPhaseShield(state: MatchState, caster: Squad, def: AbilityDef): void {
  const radius = def.shieldRadius ?? 140;
  const absorb = def.shieldAbsorb ?? 200;
  const durationSec = def.durationSec;
  const id = mintEntityId(state);
  state.shieldBubbles[id] = {
    id,
    ownerId: caster.ownerId,
    pos: { x: caster.pos.x, y: caster.pos.y },
    radius,
    remaining: absorb,
    expiresAtTick: tickOf(state.tick + secondsToTicks(durationSec)),
  };
}

function reapExpiredBubbles(state: MatchState): void {
  const dead: EntityId[] = [];
  for (const bubble of Object.values(state.shieldBubbles)) {
    if (bubble.remaining <= 0 || state.tick >= bubble.expiresAtTick) dead.push(bubble.id);
  }
  for (const id of dead) delete state.shieldBubbles[id];
}

function reapExpiredMiniTurrets(state: MatchState): void {
  const dead: EntityId[] = [];
  for (const b of Object.values(state.buildings)) {
    if (b.kind !== 'mini_turret') continue;
    if (state.tick >= b.expiresAtTick) dead.push(b.id);
  }
  for (const id of dead) delete state.buildings[id];
}

// Public read helpers — used by Renderer / HudPanel / tests so they don't
// reach into the abilities-data tables themselves.
export function abilityIdFor(squad: Squad): AbilityId {
  return ARCHETYPE_ABILITY[squad.archetype];
}

export function abilityDefFor(squad: Squad): AbilityDef {
  return ABILITY_DEFS[ARCHETYPE_ABILITY[squad.archetype]];
}

// True if a target is currently marked AND the marker is `byPlayerId`. The damage
// bonus and through-fog visibility are scoped to the marker, not symmetric.
export function isMarkedFor(state: MatchState, target: Squad, byPlayerId: PlayerId): boolean {
  if (target.markedByPlayerId !== byPlayerId) return false;
  return state.tick < target.markedUntilTick;
}

function secondsToTicks(seconds: number): number {
  return Math.max(1, Math.round(seconds / SECONDS_PER_TICK));
}

function sqDist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function mintEntityId(state: MatchState): EntityId {
  const id = state.nextEntityId;
  state.nextEntityId = id + 1;
  return entityId(id);
}
